import {
	ChangeDetectionStrategy,
	Component,
	inject,
	output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
	MAT_DATE_LOCALE,
	provideNativeDateAdapter,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { debounceTime } from 'rxjs';
import { OperatorStateService } from '../../../services/state-services/operator-state.service';

export type ReservationCreateMode = 'phone' | 'walk_in';

@Component({
	selector: 'app-operator-reservation-controls',
	imports: [ReactiveFormsModule, MatDatepickerModule],
	providers: [
		provideNativeDateAdapter(),
		{ provide: MAT_DATE_LOCALE, useValue: 'el-GR' },
	],
	templateUrl: './reservation-controls.component.html',
	styleUrl: './reservation-controls.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperatorReservationControlsComponent {
	readonly state = inject(OperatorStateService);
	readonly createRequested = output<ReservationCreateMode>();
	readonly dateChanged = output<void>();
	readonly searchControl = new FormControl('', { nonNullable: true });

	constructor() {
		this.searchControl.valueChanges
			.pipe(debounceTime(300), takeUntilDestroyed())
			.subscribe((value) => {
				this.state.search.set(value);
				void this.state.loadDay();
			});
	}

	materialDateValue(value: string): Date {
		const [year, month, day] = value.split('-').map(Number);
		return new Date(year, month - 1, day);
	}

	async changeMaterialDate(value: Date | null): Promise<void> {
		if (!value) return;
		const year = value.getFullYear();
		const month = String(value.getMonth() + 1).padStart(2, '0');
		const day = String(value.getDate()).padStart(2, '0');
		await this.changeDate(`${year}-${month}-${day}`);
	}

	formatServiceDate(value: string): string {
		const [year, month, day] = value.split('-');
		return `${day} / ${month} / ${year}`;
	}

	async shiftDate(days: number): Promise<void> {
		const [year, month, day] = this.state.serviceDate().split('-').map(Number);
		const date = new Date(Date.UTC(year, month - 1, day));
		date.setUTCDate(date.getUTCDate() + days);
		await this.changeDate(date.toISOString().slice(0, 10));
	}

	private async changeDate(value: string): Promise<void> {
		if (!value) return;
		this.state.serviceDate.set(value);
		this.dateChanged.emit();
		await this.state.loadDay();
	}
}
